class ShoppableScene extends HTMLElement {
  handleDocumentClickBound = this.handleDocumentClick.bind(this);
  handleKeydownBound = this.handleKeydown.bind(this);
  handleViewportChangeBound = this.handleViewportChange.bind(this);

  connectedCallback() {
    this.hotspots = [...this.querySelectorAll('[data-hotspot]')];
    this.allProductsButton = this.querySelector('[data-all-products]');
    this.addAllButtons = [...this.querySelectorAll('[data-add-all-to-cart]')];
    this.sheet = this.querySelector('[data-product-sheet]');
    this.sheetCloseButtons = [...this.querySelectorAll('[data-sheet-close]')];
    this.sheetHome = this.sheet?.parentElement || this;
    this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.sheetClosing = false;

    this.hotspots.forEach((hotspot) => {
      const trigger = hotspot.querySelector('[data-hotspot-trigger]');
      trigger?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleHotspot(hotspot);
      });
    });

    this.allProductsButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.handleAllProductsClick();
    });

    this.addAllButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.addAllToCart();
      });
    });

    this.sheetCloseButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.closeSheet();
      });
    });

    this.media = this.querySelector('.shoppable-scene__media');
    this.media?.addEventListener('click', (event) => this.handleMediaClick(event));
    this.addEventListener('click', (event) => event.stopPropagation());

    document.addEventListener('click', this.handleDocumentClickBound);
    this.mobileQuery.addEventListener('change', this.handleViewportChangeBound);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClickBound);
    document.removeEventListener('keydown', this.handleKeydownBound);
    this.mobileQuery?.removeEventListener('change', this.handleViewportChangeBound);
    this.closeSheet(true);
  }

  isMobile() {
    return this.mobileQuery.matches;
  }

  handleAllProductsClick() {
    if (this.sheet && this.isMobile()) {
      this.toggleSheet();
      return;
    }

    this.toggleAllHotspots();
  }

  handleMediaClick(event) {
    const interactive = event.target.closest(
      '[data-hotspot-trigger], [data-hotspot-card], [data-all-products], [data-product-sheet], .shoppable-scene__content'
    );

    if (interactive) return;

    this.closeAllHotspots();
  }

  handleDocumentClick(event) {
    if (this.contains(event.target) || this.sheet?.contains(event.target)) return;
    this.closeAllHotspots();
    this.closeSheet();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeSheet();
    }
  }

  handleViewportChange() {
    if (!this.isMobile()) {
      this.closeSheet(true);
    }
  }

  toggleHotspot(hotspot) {
    const isOpen = hotspot.classList.contains('is-open');

    if (isOpen) {
      this.setHotspotOpen(hotspot, false);
      return;
    }

    this.hotspots.forEach((item) => {
      if (item !== hotspot) {
        this.setHotspotOpen(item, false);
      }
    });

    this.setHotspotOpen(hotspot, true);
  }

  toggleAllHotspots() {
    const shouldOpen = this.hotspots.some((hotspot) => !hotspot.classList.contains('is-open'));
    this.hotspots.forEach((hotspot) => this.setHotspotOpen(hotspot, shouldOpen));
  }

  closeAllHotspots() {
    this.hotspots.forEach((hotspot) => this.setHotspotOpen(hotspot, false));
  }

  setHotspotOpen(hotspot, open) {
    const trigger = hotspot.querySelector('[data-hotspot-trigger]');
    hotspot.classList.toggle('is-open', open);
    trigger?.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.syncAllProductsState();
  }

  isSheetOpen() {
    return Boolean(this.sheet && this.sheet.classList.contains('is-open'));
  }

  toggleSheet() {
    if (this.isSheetOpen()) {
      this.closeSheet();
      return;
    }

    this.openSheet();
  }

  openSheet() {
    if (!this.sheet || !this.isMobile()) return;

    this.closeAllHotspots();
    this.sheetClosing = false;
    document.body.appendChild(this.sheet);
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
    this.sheet.hidden = false;
    this.sheet.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this.handleKeydownBound);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.sheet.classList.add('is-open');
        this.syncAllProductsState();
        this.sheet.querySelector('.shoppable-scene__sheet-close')?.focus();
      });
    });
  }

  closeSheet(immediate = false) {
    if (!this.sheet || this.sheet.hidden) return;

    this.sheet.classList.remove('is-open');
    this.sheet.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this.handleKeydownBound);
    this.syncAllProductsState();

    const finish = () => {
      if (!this.sheetClosing && !immediate) return;
      this.sheetClosing = false;
      this.sheet.hidden = true;
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
      this.sheetHome?.appendChild(this.sheet);
      this.allProductsButton?.focus();
    };

    if (immediate) {
      this.sheetClosing = false;
      this.sheet.hidden = true;
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
      this.sheetHome?.appendChild(this.sheet);
      return;
    }

    this.sheetClosing = true;
    const panel = this.sheet.querySelector('.shoppable-scene__sheet-panel');
    panel?.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, 450);
  }

  syncAllProductsState() {
    if (!this.allProductsButton) return;

    const sheetOpen = this.isSheetOpen();
    const allOpen = this.hotspots.length > 0 && this.hotspots.every((hotspot) => hotspot.classList.contains('is-open'));
    const active = this.isMobile() ? sheetOpen : allOpen;

    this.allProductsButton.classList.toggle('is-active', active);
    this.allProductsButton.setAttribute('aria-pressed', active ? 'true' : 'false');
    this.allProductsButton.setAttribute('aria-expanded', active ? 'true' : 'false');
  }

  getCartSectionIds() {
    if (this.cart?.getSectionsToRender) {
      return this.cart.getSectionsToRender().map((section) => section.id);
    }

    return ['cart-icon-bubble'];
  }

  updateCartSections(parsedState) {
    if (!parsedState?.sections) return;

    const cartIcon = document.getElementById('cart-icon-bubble');
    const iconHtml = parsedState.sections['cart-icon-bubble'];

    if (cartIcon && iconHtml) {
      const doc = new DOMParser().parseFromString(iconHtml, 'text/html');
      const source = doc.querySelector('.shopify-section') || doc.body;
      cartIcon.innerHTML = source.innerHTML;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer?.getSectionsToRender) return;

    cartDrawer.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-icon-bubble') return;

      const html = parsedState.sections[section.id];
      if (!html) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const inner = cartDrawer.getSectionInnerHTML(html, section.selector);
      if (inner) sectionElement.innerHTML = inner;
    });

    cartDrawer.classList.remove('is-empty');
    cartDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
  }

  getVariantIds() {
    return [...new Set(
      this.hotspots
        .map((hotspot) => hotspot.dataset.variantId)
        .filter((variantId) => variantId && variantId !== 'null' && variantId !== '')
    )];
  }

  setAddAllLoading(isLoading) {
    this.addAllButtons.forEach((button) => {
      button.classList.toggle('is-loading', isLoading);
      button.disabled = isLoading;
      button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
    });
  }

  async addAllToCart() {
    const variantIds = this.getVariantIds();
    if (!variantIds.length || !this.addAllButtons.length) return;

    if (this.addAllButtons.some((button) => button.classList.contains('is-loading'))) return;

    this.setAddAllLoading(true);

    const items = variantIds.map((id) => ({
      id: parseInt(id, 10),
      quantity: 1,
    }));

    const payload = {
      items,
      sections: this.getCartSectionIds(),
      sections_url: window.location.pathname,
    };

    if (this.cart?.setActiveElement) {
      const activeButton = this.addAllButtons.find((button) => button.classList.contains('is-loading')) || this.addAllButtons[0];
      this.cart.setActiveElement(activeButton);
    }

    try {
      const response = await fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(window.PUB_SUB_EVENTS.cartError, {
            source: 'shoppable-scene',
            errors: data.description,
            message: data.message,
          });
        }
        return;
      }

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'shoppable-scene',
          cartData: data,
        });
      }

      this.updateCartSections(data);
      this.closeSheet();
    } catch (error) {
      console.error(error);
    } finally {
      this.setAddAllLoading(false);
    }
  }
}

if (!customElements.get('shoppable-scene')) {
  customElements.define('shoppable-scene', ShoppableScene);
}
